import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import map from 'lodash/map'
import filter from 'lodash/filter'
import classnames from 'classnames'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import sortSearchResults from '@Utils/SortSearchResults'
import TranslationLanguageTts from './TranslationLanguageTts'
import Arrow from '@Images/languages_dropdown_arrow.svg'
import Alert from '@Images/project_setup_form_alert.svg'

import '@SiteComponents/SearchDropdown/SearchDropdown.scss'
import './TranslationLanguagesTts.scss'

const DROPDOWN_OFFSET_Y = 50 // px

export interface IDropdownPosition {
  top: string
  left: string
}

interface TranslationLanguagesTtsProps {
  readonly isNewProject: boolean
  readonly newProjectVersionLanguage: string
  readonly existingProjectVersionLanguage: string
  readonly newTranslationLanguages: string[]
  readonly existingTranslationLanguages: string[]
  readonly availableTranslationLanguages: string[]
  readonly showAlert: boolean
  readonly translationProvider: string
  readonly fetchingTranslationLanguages: boolean
  setNewTranslationLanguages(newTranslationLanguages: string[]): void
  handleFetchTranslationLanguages(): void
}

const TranslationLanguagesTts: React.FC<TranslationLanguagesTtsProps> = props => {
  const {
    isNewProject,
    newProjectVersionLanguage,
    existingProjectVersionLanguage,
    newTranslationLanguages,
    existingTranslationLanguages,
    availableTranslationLanguages,
    showAlert,
    translationProvider,
    fetchingTranslationLanguages,
  } = props

  const [showAddTranslationLanguages, setShowAddTranslationLanguages] = useState(false)
  const [searchTextForAdding, setSearchTextForAdding] = useState('')
  const [searchTextForReplacing, setSearchTextForReplacing] = useState('')
  const [relevantTranslationLanguages, setRelevantTranslationLanguages] = useState(availableTranslationLanguages)
  const [inputForAddingIsFocused, setInputForAddingIsFocused] = useState(false)
  const [inputForReplacingIsFocused, setInputForReplacingIsFocused] = useState(false)
  const [indexForReplacingTranslationLanguages, setIndexForReplacingTranslationLanguages] = useState(-1)

  const projectVersionLanguage = isNewProject ? newProjectVersionLanguage : existingProjectVersionLanguage
  const hasRelevantTranslationLanguages = relevantTranslationLanguages.length > 1 || (relevantTranslationLanguages.length === 1 && relevantTranslationLanguages[0] !== projectVersionLanguage) // Do not only show the source language
  const languageWireIsTranslationProvider = translationProvider === 'language_wire'

  const handleCloseNewTranslationLanguages = () => {
    setIndexForReplacingTranslationLanguages(-1)
  }

  useClickOutsideListener('TranslationLanguagesTts__add-element', setShowAddTranslationLanguages, showAddTranslationLanguages)
  useClickOutsideListener('TranslationLanguagesTts__new-translations-item-main-element', handleCloseNewTranslationLanguages, indexForReplacingTranslationLanguages !== -1)

  useEffect(() => {
    if (languageWireIsTranslationProvider) {
      props.setNewTranslationLanguages([])
      props.handleFetchTranslationLanguages()
    } else {
      props.setNewTranslationLanguages(
        filter(newTranslationLanguages, currentNewTranslationLanguage => currentNewTranslationLanguage !== newProjectVersionLanguage)
      )
    }
  }, [newProjectVersionLanguage])

  useEffect(() => {
    const searchText = searchTextForAdding.length > 0 ? searchTextForAdding : searchTextForReplacing
    const newRelevantTranslationLanguages = filter(
      availableTranslationLanguages,
      language => language.toLowerCase().includes(searchText.toLowerCase())
    )

    newRelevantTranslationLanguages.sort(sortSearchResults(searchText))

    setRelevantTranslationLanguages(newRelevantTranslationLanguages)
  }, [availableTranslationLanguages, searchTextForAdding, searchTextForReplacing])

  useEffect(() => {
    if (!showAddTranslationLanguages) {
      setSearchTextForAdding('')
    }
  }, [showAddTranslationLanguages])

  useEffect(() => {
    const scrollContainer = document.querySelector('.TranslationLanguagesTts__new-translations-items') as HTMLDivElement
    const hasClosedTranslationLanguages = indexForReplacingTranslationLanguages === -1
    if (hasClosedTranslationLanguages) {
      setSearchTextForReplacing('')
    }

    if (scrollContainer) {
      scrollContainer.style.overflowY = hasClosedTranslationLanguages ? 'scroll' : 'hidden'
    }
  }, [indexForReplacingTranslationLanguages])

  const toggleDropdown = () => {
    setShowAddTranslationLanguages(!showAddTranslationLanguages)
  }

  const renderSearchInput = (currentTranslationLanguage?: string) => {
    const isInputForReplacing = !!currentTranslationLanguage

    const handleOnClick = () => {
      if (isInputForReplacing || showAddTranslationLanguages) { return }

      setShowAddTranslationLanguages(true)
    }

    const handleSetFocus = () => {
      isInputForReplacing ? setInputForReplacingIsFocused(true) : setInputForAddingIsFocused(true)
    }

    const handleRemoveFocus = () => {
      isInputForReplacing ? setInputForReplacingIsFocused(false) : setInputForAddingIsFocused(false)
    }

    const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchText = (e.target as HTMLInputElement).value
      isInputForReplacing ? setSearchTextForReplacing(newSearchText) : setSearchTextForAdding(newSearchText)
    }

    return (
      <div
        className={classnames(
          'SearchDropdown__input-wrapper',
          { 'SearchDropdown__input-wrapper-focused': isInputForReplacing ? inputForReplacingIsFocused : inputForAddingIsFocused },
          { 'SearchDropdown__input-wrapper-alert': showAlert && !isInputForReplacing && !inputForAddingIsFocused },
        )}
        onClick={handleOnClick}
      >
        <input
          placeholder={isNewProject ? 'Add a new language...' : 'Select...'}
          autoComplete="off"
          value={isInputForReplacing ? searchTextForReplacing : searchTextForAdding}
          onChange={handleSearchTextChange}
          onFocus={handleSetFocus}
          onBlur={handleRemoveFocus}
          disabled={fetchingTranslationLanguages}
        />
        <Arrow
          onClick={toggleDropdown}
        />
      </div>
    )
  }

  const renderTranslationLanguagesList = (languageIndexToReplace?: number) => {
    const isLanguagesListForReplacing = typeof(languageIndexToReplace) === 'number'

    return (
      map(relevantTranslationLanguages, (relevantTranslationLanguage, index) => {
        const isProjectVersionLanguage = projectVersionLanguage === relevantTranslationLanguage
        const alreadyAddedTranslationLanguage = existingTranslationLanguages.concat(newTranslationLanguages).includes(relevantTranslationLanguage)
        const isSelectable = !isProjectVersionLanguage && !alreadyAddedTranslationLanguage

        const handleAddToNewTranslationLanguages = () => {
          if (!isSelectable) { return }

          if (isLanguagesListForReplacing) {
            props.setNewTranslationLanguages([...newTranslationLanguages.slice(0, languageIndexToReplace), relevantTranslationLanguage , ...newTranslationLanguages.slice(languageIndexToReplace + 1)])
            setIndexForReplacingTranslationLanguages(-1)
          } else {
            props.setNewTranslationLanguages(newTranslationLanguages.concat(relevantTranslationLanguage))
            setShowAddTranslationLanguages(false)
          }
        }

        return (
          <li
            key={index}
            className={classnames(
              'SearchDropdown__item',
              { 'SearchDropdown__item-selectable': isSelectable }
            )}
            onClick={handleAddToNewTranslationLanguages}
          >
            <div className="SearchDropdown__item-name">
              {relevantTranslationLanguage}
            </div>
            <div className="SearchDropdown__item-info">
              {isProjectVersionLanguage && 'Source language'}
              {alreadyAddedTranslationLanguage && 'Already added'}
            </div>
          </li>
        )
      })
    )
  }

  const renderNoOptionFound = () => {
    return (
      <li className="SearchDropdown__item SearchDropdown__item-not-found">
        <div className="SearchDropdown__item-name">
          No language found
        </div>
      </li>
    )
  }

  const renderTranslationLanguages = (style: IDropdownPosition, languageIndexToReplace?: number) => {
    const isDropdownForReplacing = typeof(languageIndexToReplace) === 'number'

    return ReactDOM.createPortal(
      (
        <ul
          className={classnames(
            'SearchDropdown__items',
            { 'TranslationLanguagesTts__add-element': !isDropdownForReplacing },
            { 'TranslationLanguagesTts__new-translations-item-element': isDropdownForReplacing },
          )}
          style={style}
        >
          {hasRelevantTranslationLanguages ? renderTranslationLanguagesList(languageIndexToReplace) : renderNoOptionFound()}
        </ul>
      ),
      document.getElementById('site-container')!
    )
  }

  const newTranslationsList = map(newTranslationLanguages, (currentNewTranslationLanguage, index) => {
    return (
      <TranslationLanguageTts
        key={index}
        index={index}
        currentNewTranslationLanguage={currentNewTranslationLanguage}
        newTranslationLanguages={newTranslationLanguages}
        setNewTranslationLanguages={props.setNewTranslationLanguages}
        indexForReplacingTranslationLanguages={indexForReplacingTranslationLanguages}
        setIndexForReplacingTranslationLanguages={setIndexForReplacingTranslationLanguages}
        dropdownOffsetY={DROPDOWN_OFFSET_Y}
        renderSearchInput={renderSearchInput}
        renderTranslationLanguages={renderTranslationLanguages}
      />
    )
  })

  const renderNewTranslationLanguages = () => {
    return (
      <ul className="TranslationLanguagesTts__new-translations-items">
        {newTranslationsList}
      </ul>
    )
  }

  const renderAlert = () => {
    return (
      <div className="SetupProjectPopup__alert">
        <Alert />
        You need to select the language you want to translate your voice-over into.
      </div>
    )
  }

  const dropdownParentElement = document.getElementById('translation_languages_add')
  let dropdownX = 0
  let dropdownY = 0

  if (dropdownParentElement) {
    const dropdownParentElementBounds = dropdownParentElement.getBoundingClientRect()
    dropdownX = dropdownParentElementBounds.left
    dropdownY = dropdownParentElementBounds.top + DROPDOWN_OFFSET_Y
  }

  const dropdownPositioningStyle = {
    top: `${dropdownY}px`,
    left: `${dropdownX}px`,
  }

  return (
    <div className="TranslationLanguagesTts">
      {renderNewTranslationLanguages()}
      <div
        className="TranslationLanguagesTts__add TranslationLanguagesTts__add-element"
        id="translation_languages_add"
      >
        {renderSearchInput()}
        {showAddTranslationLanguages && renderTranslationLanguages(dropdownPositioningStyle)}
      </div>
      {showAlert && renderAlert()}
    </div>
  )
}

export default TranslationLanguagesTts
